import { AccountMembership } from '@mkp/account-membership/data-access';
import { LoadingState } from '@mkp/shared/util-state';
import { createSelector } from '@ngrx/store';
import { User } from '@mkp/user/models';
import { accountMembershipFeature as fromAccountMembership } from './account-membership.reducer';

export const {
  selectAll: selectAccountMemberships,
  selectEntities,
  selectTotalCount,
  selectLinks,
  selectFilter,
  selectLoaded,
  selectFeatureState,
  selectSelectedEntity: selectSelectedAccountMembership,
} = fromAccountMembership.selectors;

export const selectAccountMembershipByUserId = (userIdToFind: User['id']) =>
  createSelector(selectAccountMemberships, (memberships) =>
    memberships.find(({ userId }) => userId === userIdToFind)
  );

export const selectAccountMembershipByAccountId = (accountId: string | undefined) =>
  createSelector(selectAccountMemberships, (memberships: AccountMembership[]) =>
    memberships.filter((membership) => membership.accountId === accountId)
  );

export const selectActiveAccountMembershipCount = createSelector(
  selectFeatureState,
  (state) => state.totalCount
);

/**
 * The membership count together with whether it can be trusted yet, as one value.
 *
 * `totalCount` is `null` both before the bootstrap request and after it fails, so a consumer that
 * waits for a non-null count waits forever on the failure path. It has to read the fetch state
 * too — and it has to read both from a single selector: combining two `store.select` streams
 * lets `combineLatest` emit a half-updated pair, where the fetch reads as settled while the count
 * is still the previous `null`.
 */
export const selectAccountMembershipCountState = createSelector(
  selectLoaded,
  selectActiveAccountMembershipCount,
  (settled, count) => ({ settled, count })
);

export const selectHasActiveAccountMembership = createSelector(
  selectActiveAccountMembershipCount,
  (activeAccountMembershipCount) => !!activeAccountMembershipCount
);

export const selectAccountMembershipIsLoading = createSelector(
  selectFeatureState,
  (state) => state.fetchState === LoadingState.LOADING
);
